import { useState } from 'react'

import { startCase } from 'lodash'

import HorizontalBarChart, { HChartSeries } from '@/components/ui/charts/horizontal-bar-chart/HorizontalBarChart'
import Drilldown from '@/insights/components/Drilldown'
import { severityCodes, getSeverityLabel } from '@/insights/components/SeverityDropdown'
import { InsightConfigurationType } from '@/types/dashboard-types'

import type { ReeferAlarmCountData } from './ReeferAlarmCountBySeverity'
import { ReeferAlarmCountBySeverityParams } from './ReeferAlarmCountBySeverity'

type SeverityType = 'critical' | 'warning' | 'informational'

const HorizontalChart = ({
  configuration,
  data,
}: {
  configuration: InsightConfigurationType
  data: ReeferAlarmCountData[]
}) => {
  const [activeDrilldown, setActiveDrilldown] = useState<InsightConfigurationType | null>(null)

  const severityColorMap = {
    critical: '#F18484',
    warning: '#FFC75C',
    informational: '#6AC380',
  }

  const isSmallCard = ['tiny', 'mini'].includes(configuration.width)

  const yAxisData = data.sort((a, b) => a.total - b.total).map((item) => item.companyName)
  const severities = (
    (configuration.parameters?.severity as number[])?.length
      ? (configuration.parameters.severity as number[]).map((s) => getSeverityLabel(String(s)).toLowerCase())
      : ['critical', 'warning', 'informational']
  ) as SeverityType[]

  const formatLabel = (params: { value: number }): string => {
    const val = params.value
    if (val < 1000) return ''
    if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M'
    if (val >= 1000) return (val / 1000).toFixed(1) + 'K'
    return val.toString()
  }

  const series: HChartSeries[] = severities.map((s) => ({
    name: startCase(s),
    data: data.map((item) => item[s]),
    color: severityColorMap[s],

    label: {
      show: !isSmallCard,
      position: 'insideLeft',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      formatter: formatLabel as any,
      color: '#000',
    },

    stack: 'alarms',
  }))

  const handleBarClick = (params: { seriesName: string; name: string }) => {
    const clickedSeverity = params.seriesName.toLowerCase()
    const parameters = configuration.parameters as ReeferAlarmCountBySeverityParams
    const clickedCompany = data.find((item) => item.companyName === params.name)

    const severityCode = severityCodes.find((s) => s.label.toLowerCase() === clickedSeverity)?.value

    const ddConfig: InsightConfigurationType = {
      ...configuration,
      slug: 'alarm/alarms-detail',
      title: `${params.name} - ${params.seriesName} Alarms`,
      parameters: {
        ...parameters,
        companyId: clickedCompany?.companyId, // ← Use companyId instead of makes
        displayFormat: 'table',
        severity: severityCode,
        isDrilldown: true,
      },
    }

    setActiveDrilldown(ddConfig)
  }

  return (
    <>
      {activeDrilldown && <Drilldown configuration={activeDrilldown} onClose={() => setActiveDrilldown(null)} />}
      <HorizontalBarChart yAxisData={yAxisData} series={series} onBarClick={handleBarClick} />
    </>
  )
}

export default HorizontalChart
